/* RESPONSIBLE TEAM: team-fin-voice */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { requestHeaders } from 'embercom/lib/ajax';
import { DEFAULT_VOICE } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';

export default class AudioPreviewService extends Service {
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: $TSFixMe;

  @tracked isPlaying = false;
  private currentAudioUrl: string | null = null;
  private currentAudio: HTMLAudioElement | null = null;

  async play(
    term: string,
    pronunciation: string,
    translationType: string,
    voice = DEFAULT_VOICE,
    language = 'en-US',
  ): Promise<void> {
    try {
      this.isPlaying = true;
      this.cleanup();
      let response = await fetch('/ember/fin_voice/pronunciations/preview_audio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...requestHeaders(),
        },
        body: JSON.stringify({
          app_id: this.appService.app.id,
          term,
          pronunciation: `${pronunciation}.`,
          translation_type: translationType,
          voice,
          language,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      let arrayBuffer = await response.arrayBuffer();
      let blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      this.currentAudioUrl = URL.createObjectURL(blob);
      this.currentAudio = new Audio(this.currentAudioUrl);

      await new Promise<void>((resolve, reject) => {
        if (!this.currentAudio) {
          resolve();
          return;
        }

        this.currentAudio.onended = () => resolve();
        this.currentAudio.onerror = () => reject(new Error('Audio playback failed'));
        this.currentAudio.play().catch(reject);
      });
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.pronunciation-manager.preview-error'),
      );
    } finally {
      this.isPlaying = false;
    }
  }

  stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }
    this.cleanup();
    this.isPlaying = false;
  }

  private cleanup(): void {
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = null;
    }
    this.currentAudio = null;
  }
}

declare module '@ember/service' {
  interface Registry {
    'audio-preview': AudioPreviewService;
  }
}
